import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { Task, UpdateTaskRequest, TransferDirection, PostPickupAction, DeliveryMode } from '../../types/task';
import { Connection } from '../../types/connection';
import { connectionsApi } from '../../services/api/connections';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '../../lib/queryKeys';
import { remotePathWording } from '../../utils/taskConfigDisplay';

// Stable empty reference: `?? []` would be a new array every render.
const NO_CONNECTIONS: Connection[] = [];

export interface TaskEditorProps {
  task: Task;
  partners: Array<{ id: string; name: string }>;
  onSave: (taskId: string, taskData: UpdateTaskRequest) => Promise<void>;
  onCancel: () => void;
  onValidateConfiguration?: (transferDirection: TransferDirection, configuration: any) => Promise<{ valid: boolean; errors: string[] }>;
}

export function TaskEditor({
  task,
  partners,
  onSave,
  onCancel,
  onValidateConfiguration
}: TaskEditorProps) {
  const [formData, setFormData] = useState({
    name: task.name,
    description: task.description || '',  // THUB-160: editable display label
    partnerId: task.partnerId,
    connectionId: task.connectionId,
    transferDirection: task.transferDirection,
    schedule: task.schedule || {
      type: 'manual' as const,
      expression: '',
      enabled: false,
      timezone: 'UTC'
    },
    configuration: task.configuration || {}
  });

  // PGP encryption state
  const [pgpEnabledRequested, setPgpEnabled] = useState(task.pgpEnabled || false);

  const [errors, setErrors] = useState<string[]>([]);
  const [isValidating, setIsValidating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // FR-034/035: Post-pickup action state (Remote Inbound tasks)
  const [postPickupAction, setPostPickupAction] = useState<PostPickupAction>(task.postPickupAction || 'none');
  const [postPickupTargetPath, setPostPickupTargetPath] = useState<string>(task.postPickupTargetPath || '');

  // FR-042/043: Delivery mode state (Remote Outbound tasks)
  const [deliveryMode, setDeliveryMode] = useState<DeliveryMode>(task.deliveryMode || 'immediate');
  const [deliverySchedule, setDeliverySchedule] = useState<string>(task.deliverySchedule || '');

  // Connection-related state
  // THUB-298: connections for the selected partner are a QUERY keyed on that partner, not a
  // useState trio filled by an effect. Keyed under `connections` so a connection change elsewhere
  // reaches this dropdown, and `enabled` keeps it from firing before a partner is chosen.
  //
  // THUB-298 follow-up: this MUST use connectionsApi.getConnectionsByPartner (GET /connections),
  // not partnersApi.getPartnerConnections (GET /partners/{id}/connections). The partner-scoped
  // route is served by partner_management_api, which never computes pgpPublicKeyConfigured /
  // pgpPrivateKeyConfigured, and its hand-rolled mapper whitelists a fixed field set that drops
  // them anyway -- so `pgpKeyAvailable` below was permanently false and the "Enable PGP
  // encryption" checkbox never rendered in Edit Task. The /connections route runs the canonical
  // transformConnection, which carries both flags.
  //
  // The key is DELIBERATELY not connections.byPartner: PartnerDetails reads that key with the
  // partner-scoped endpoint, and two shapes under one key is the cache-aliasing bug class that
  // already bit the task-execution reads. Different shape -> different key.
  const connectionsQuery = useQuery({
    queryKey: queryKeys.connections.byPartnerDetailed(formData.partnerId ?? ''),
    queryFn: () => connectionsApi.getConnectionsByPartner(formData.partnerId!),
    enabled: !!formData.partnerId,
  });
  const availableConnections: Connection[] = connectionsQuery.data ?? NO_CONNECTIONS;
  const loadingConnections = !!formData.partnerId && connectionsQuery.isPending;
  // THUB-298 follow-up: a partner with zero connections is a distinct, actionable state -- the
  // operator needs to know to go create one, not stare at an empty dropdown with no explanation.
  // Only report it once the query has actually resolved, so it does not flash during the fetch.
  //
  // These are kept SEPARATE because they need different affordances: a failed fetch is a platform
  // problem worth retrying, an empty list is a normal state that Retry cannot fix. Rendering both
  // as a danger-styled error with a Retry button made "this partner has no connections yet" look
  // like an outage.
  const connectionFetchError = connectionsQuery.error
    ? 'Failed to load connections for this partner.'
    : '';
  const connectionsEmpty = connectionsQuery.isSuccess && availableConnections.length === 0;

  // THUB-298: declared BEFORE the effect that calls it, and memoized.
  //
  // It used to be defined further down the component, so the effect referenced it before its
  // declaration (react-hooks/immutability). useCallback with an empty dep list is correct here:
  // the body reads only its `partnerId` argument and stable setState functions, so the identity
  // never needs to change -- which also lets the effect below list it as a real dependency
  // instead of suppressing exhaustive-deps.

  // Fetch connections when partner is selected

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // If partner changes, reset connection and fetch new connections
    if (field === 'partnerId') {
      setFormData(prev => ({
        ...prev,
        partnerId: value,
        connectionId: '' // Reset connection when partner changes
      }));
    }
    
    // Clear errors when user makes changes
    if (errors.length > 0) {
      setErrors([]);
    }
  };

  const handleScheduleChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      schedule: {
        ...prev.schedule,
        [field]: value
      }
    }));
  };

  const handleConfigurationChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      configuration: {
        ...prev.configuration,
        [field]: value
      }
    }));
  };

  // Get selected connection for context
  const selectedConnection = availableConnections.find(
    conn => conn.connectionId === formData.connectionId
  );
  const selectedHostingType = selectedConnection?.hostingType;

  // THUB-203: PGP is only offered when the connection has the key required for
  // this direction (public for outbound, private for inbound). The carve-out
  // (task.pgpEnabled) keeps the toggle visible for an already-enabled task so it
  // can still be turned off even if the key was later removed.
  const pgpKeyAvailable = selectedConnection
    ? (formData.transferDirection === 'outbound'
        ? !!selectedConnection.pgpPublicKeyConfigured
        : !!selectedConnection.pgpPrivateKeyConfigured)
    : false;
  // THUB-298: the "never submit a hidden pgpEnabled" rule is DERIVED, not enforced by an effect
  // that corrected state after the fact.
  //
  // `pgpEnabledRequested` is what the operator asked for; `pgpEnabled` is what we will actually
  // submit, which requires the key to be available (or the task to have had it enabled already,
  // so it can still be turned OFF). The old effect reset the flag one render LATER, so a submit
  // in that window sent a pgpEnabled the API rejects -- the exact failure the effect was written
  // to prevent. A derivation has no such window.
  const pgpEnabled = pgpEnabledRequested && (pgpKeyAvailable || !!task.pgpEnabled);
  const showPgpToggle = pgpKeyAvailable || pgpEnabled;

  // T090: Check if this is a Remote Inbound task (remote hosting + inbound direction)
  const isRemoteInboundTask = (): boolean => {
    return selectedHostingType === 'remote' && formData.transferDirection === 'inbound';
  };

  // T091: Check if this is a Remote Outbound task (remote hosting + outbound direction)
  const isRemoteOutboundTask = (): boolean => {
    return selectedHostingType === 'remote' && formData.transferDirection === 'outbound';
  };

  // THUB-220: scheduled (batch) delivery is only wired for SFTP remote outbound; for azure_blob
  // (and other non-SFTP methods) file_processing always delivers immediately, so offering
  // "scheduled" would silently degrade to immediate + an inert schedule. Only expose it for SFTP;
  // the backend (handle_update_task) rejects it for other methods as a backstop. Mirrors
  // TaskCreator.supportsScheduledDelivery().
  const supportsScheduledDelivery = (): boolean => {
    return selectedConnection?.transferMethod === 'sftp';
  };
  const deliveryModeOptions = (supportsScheduledDelivery()
    ? ['immediate', 'scheduled']
    : ['immediate']) as ReadonlyArray<DeliveryMode>;

  const validateForm = () => {
    const newErrors: string[] = [];

    if (!formData.name.trim()) {
      newErrors.push('Task name is required');
    }

    if (!formData.partnerId) {
      newErrors.push('Partner selection is required');
    }

    if (!formData.connectionId) {
      newErrors.push('Connection selection is required');
    }

    // Validate that selected connection exists and belongs to selected partner
    if (formData.connectionId && formData.partnerId) {
      const selectedConnection = availableConnections.find(conn => conn.connectionId === formData.connectionId);
      if (!selectedConnection) {
        newErrors.push('Selected connection is not valid for this partner');
      } else if (selectedConnection.partnerId !== formData.partnerId) {
        newErrors.push('Selected connection does not belong to the selected partner');
      }
    }

    if (formData.schedule.type === 'cron' && !formData.schedule.expression?.trim()) {
      newErrors.push('Cron expression is required for cron schedule');
    }

    if (formData.schedule.type === 'rate' && !formData.schedule.expression?.trim()) {
      newErrors.push('Rate expression is required for rate schedule');
    }

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const handleValidateConfiguration = async () => {
    if (!onValidateConfiguration) return;

    setIsValidating(true);
    try {
      const result = await onValidateConfiguration(formData.transferDirection, formData.configuration);
      if (!result.valid) {
        setErrors(result.errors);
      } else {
        setErrors([]);
        toast.success('Configuration is valid!');
      }
    } catch (error) {
      console.warn('Configuration validation failed:', error);
      setErrors(['Configuration validation unavailable']);
    } finally {
      setIsValidating(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSaving(true);
    try {
      const updateData: UpdateTaskRequest = {
        name: formData.name,
        // THUB-160: always send the trimmed display label (never undefined). The
        // backend PUT clears to null ONLY when `description` is present as "";
        // an omitted key leaves the old value unchanged (verified against
        // task_management_api tests). JSON.stringify drops undefined keys, so
        // `|| undefined` would make a user's "clear" silently no-op. Sending the
        // trimmed string ("" when cleared) makes clear work and set idempotent.
        description: formData.description.trim(),
        partnerId: formData.partnerId,
        connectionId: formData.connectionId,
        transferDirection: formData.transferDirection,
        schedule: formData.schedule,
        configuration: formData.configuration,
        pgpEnabled,
        // FR-034/035: Include post-pickup action only for Remote Inbound tasks
        ...(isRemoteInboundTask() && {
          postPickupAction,
          postPickupTargetPath: postPickupAction === 'move' ? postPickupTargetPath : undefined,
        }),
        // FR-042/043: Include delivery mode only for Remote Outbound tasks
        ...(isRemoteOutboundTask() && {
          // THUB-220: coerce to immediate for non-SFTP methods -- scheduled is SFTP-only and the
          // option is hidden for others, but a stale 'scheduled' state (e.g. an azure task saved
          // scheduled before this guard) must not be re-sent as scheduled.
          deliveryMode: supportsScheduledDelivery() ? deliveryMode : 'immediate',
          deliverySchedule: (supportsScheduledDelivery() && deliveryMode === 'scheduled')
            ? deliverySchedule : undefined,
        }),
      };

      await onSave(task.taskId, updateData);
      // Success - the parent component should handle closing the modal
    } catch (error) {
      console.error('Failed to update task:', error);
      setErrors(['Failed to update task. Please try again.']);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="
      bg-white dark:bg-slate-900
      rounded-lg shadow-lg
      max-w-[600px] w-full max-h-[90vh] overflow-y-auto
    ">
      <div className="
        flex justify-between items-center
        px-6 py-4
        border-b border-neutral-200 dark:border-slate-700
        bg-neutral-50 dark:bg-slate-800
        rounded-t-lg
      ">
        <h2 className="m-0 text-xl font-semibold text-neutral-900 dark:text-neutral-100">
          Edit Task
        </h2>
        <button
          type="button"
          onClick={onCancel}
          className="
            bg-transparent border-none text-2xl
            text-neutral-500 dark:text-neutral-400 cursor-pointer
            p-1 w-8 h-8 flex items-center justify-center rounded
            hover:bg-neutral-200 dark:hover:bg-slate-700
            hover:text-neutral-700 dark:hover:text-neutral-200
          "
          disabled={isSaving}
        >
          ×
        </button>
      </div>

      <form onSubmit={handleSubmit} className="p-6">
        {errors.length > 0 && (
          <div className="
            bg-danger-50 dark:bg-danger-900/20
            border border-danger-200 dark:border-danger-800
            rounded-md p-4 mb-6
          ">
            {errors.map((error, index) => (
              <div key={index} className="text-danger-600 dark:text-danger-400 text-sm mb-1 last:mb-0">
                {error}
              </div>
            ))}
          </div>
        )}

        <div className="mb-8 last:mb-0">
          <h3 className="
            m-0 mb-4 text-lg font-semibold text-neutral-700 dark:text-neutral-300
            border-b border-neutral-200 dark:border-neutral-700 pb-2
          ">
            Basic Information
          </h3>

          <div className="mb-4">
            <label htmlFor="taskName" className="
              block mb-2 text-sm font-medium text-neutral-700 dark:text-neutral-300
            ">
              Task Name *
            </label>
            <input
              id="taskName"
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              className="
                w-full px-3 py-2.5 border rounded-md text-sm
                transition-all duration-200
                bg-white dark:bg-neutral-900
                text-neutral-800 dark:text-neutral-200
                border-neutral-300 dark:border-neutral-600
                focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20
                disabled:bg-neutral-100 dark:disabled:bg-neutral-800
                disabled:text-neutral-500 dark:disabled:text-neutral-400
                disabled:cursor-not-allowed
              "
              disabled
              required
              title="Task name is immutable after creation — it determines the S3 upload path"
            />
            <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
              Task name cannot be changed. It determines the upload path for partners.
            </p>
          </div>

          {/* THUB-160: editable free-form display label. The name above is the
              immutable S3 prefix; this is the human-readable label shown in the UI. */}
          <div className="mb-4">
            <label htmlFor="taskDescription" className="
              block mb-2 text-sm font-medium text-neutral-700 dark:text-neutral-300
            ">
              Display Name / Description
            </label>
            <textarea
              id="taskDescription"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Enter a friendly description (optional)"
              rows={3}
              maxLength={500}
              disabled={isSaving}
              className="
                w-full px-3 py-2.5 border rounded-md text-sm
                transition-all duration-200
                bg-white dark:bg-neutral-900
                text-neutral-800 dark:text-neutral-200
                border-neutral-300 dark:border-neutral-600
                placeholder:text-neutral-400 dark:placeholder:text-neutral-500
                focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20
                disabled:bg-neutral-100 dark:disabled:bg-neutral-800 disabled:cursor-not-allowed
                resize-none
              "
            />
            <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
              A human-readable label shown across the UI. {formData.description.length}/500 characters
            </p>
          </div>

          <div className="mb-4">
            <label htmlFor="partner" className="
              block mb-2 text-sm font-medium text-neutral-700 dark:text-neutral-300
            ">
              Partner *
            </label>
            <select
              id="partner"
              value={formData.partnerId}
              onChange={(e) => handleInputChange('partnerId', e.target.value)}
              className="
                w-full px-3 py-2.5 border rounded-md text-sm
                transition-all duration-200
                bg-white dark:bg-neutral-900
                text-neutral-800 dark:text-neutral-200
                border-neutral-300 dark:border-neutral-600
                focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20
                disabled:bg-neutral-100 dark:disabled:bg-neutral-800
                disabled:text-neutral-500 dark:disabled:text-neutral-400
                disabled:cursor-not-allowed
              "
              disabled={isSaving}
              required
            >
              <option value="">Select a partner...</option>
              {partners.map(partner => (
                <option key={partner.id} value={partner.id}>
                  {partner.name}
                </option>
              ))}
            </select>
          </div>

          {/* Connection Selection - Only show after partner is selected */}
          {formData.partnerId && (
            <div className="mb-4">
              <label htmlFor="connectionId" className="
                block mb-2 text-sm font-medium text-neutral-700 dark:text-neutral-300
              ">
                Connection *
              </label>
              {loadingConnections ? (
                <div className="
                  p-3 text-center italic text-neutral-500 dark:text-neutral-400
                  bg-neutral-100 dark:bg-neutral-800
                  border border-neutral-300 dark:border-neutral-600
                  rounded-md
                ">
                  <span>Loading connections...</span>
                </div>
              ) : connectionFetchError ? (
                <div className="
                  flex items-center justify-between gap-3 p-3
                  bg-danger-50 dark:bg-danger-900/20
                  border border-danger-200 dark:border-danger-800
                  rounded-md
                ">
                  <span className="text-danger-600 dark:text-danger-400 text-sm">{connectionFetchError}</span>
                  <button
                    type="button"
                    className="
                      px-3 py-1.5 bg-primary-500 hover:bg-primary-600
                      text-white border-none rounded text-xs cursor-pointer
                      transition-colors duration-200
                      disabled:opacity-50 disabled:cursor-not-allowed
                    "
                    onClick={() => connectionsQuery.refetch()}
                    disabled={isSaving}
                  >
                    Retry
                  </button>
                </div>
              ) : connectionsEmpty ? (
                <div className="
                  p-3
                  bg-neutral-100 dark:bg-neutral-800
                  border border-neutral-300 dark:border-neutral-600
                  rounded-md
                ">
                  <span className="text-neutral-600 dark:text-neutral-300 text-sm">
                    No connections found for this partner. Please create a connection first.
                  </span>
                </div>
              ) : (
                <>
                  <select
                    id="connectionId"
                    value={formData.connectionId}
                    onChange={(e) => handleInputChange('connectionId', e.target.value)}
                    className="
                      w-full px-3 py-2.5 border rounded-md text-sm
                      transition-all duration-200
                      bg-white dark:bg-neutral-900
                      text-neutral-800 dark:text-neutral-200
                      border-neutral-300 dark:border-neutral-600
                      focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20
                      disabled:bg-neutral-100 dark:disabled:bg-neutral-800
                      disabled:text-neutral-500 dark:disabled:text-neutral-400
                      disabled:cursor-not-allowed
                    "
                    disabled={availableConnections.length === 0 || isSaving}
                    required
                  >
                    <option value="">Select a connection</option>
                    {availableConnections.map(connection => (
                      <option key={connection.connectionId} value={connection.connectionId}>
                        {connection.name} ({connection.transferMethod.toUpperCase()})
                      </option>
                    ))}
                  </select>
                  {availableConnections.length > 0 && (
                    <div className="mt-1.5">
                      <small className="text-neutral-500 dark:text-neutral-400 text-xs">
                        {availableConnections.length} connection{availableConnections.length !== 1 ? 's' : ''} available
                      </small>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          <div className="mb-4">
            <label htmlFor="transferDirection" className="
              block mb-2 text-sm font-medium text-neutral-700 dark:text-neutral-300
            ">
              Transfer Direction *
            </label>
            <select
              id="transferDirection"
              value={formData.transferDirection}
              onChange={(e) => handleInputChange('transferDirection', e.target.value as TransferDirection)}
              className="
                w-full px-3 py-2.5 border rounded-md text-sm
                transition-all duration-200
                bg-white dark:bg-neutral-900
                text-neutral-800 dark:text-neutral-200
                border-neutral-300 dark:border-neutral-600
                focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20
                disabled:bg-neutral-100 dark:disabled:bg-neutral-800
                disabled:cursor-not-allowed
              "
              disabled={isSaving}
              required
            >
              <option value="inbound">Inbound</option>
              <option value="outbound">Outbound</option>
            </select>
          </div>
        </div>

        {/* Schedule Configuration - Only show for Remote Inbound (pickup) tasks */}
        {isRemoteInboundTask() && (
          <div className="mb-8 last:mb-0">
            <h3 className="
              m-0 mb-4 text-lg font-semibold text-neutral-700 dark:text-neutral-300
              border-b border-neutral-200 dark:border-neutral-700 pb-2
            ">
              Pickup Schedule
            </h3>

            <div className="mb-4">
              <label htmlFor="scheduleType" className="
                block mb-2 text-sm font-medium text-neutral-700 dark:text-neutral-300
              ">
                Schedule Type
              </label>
              <select
                id="scheduleType"
                value={formData.schedule.type}
                onChange={(e) => handleScheduleChange('type', e.target.value)}
                className="
                  w-full px-3 py-2.5 border rounded-md text-sm
                  transition-all duration-200
                  bg-white dark:bg-neutral-900
                  text-neutral-800 dark:text-neutral-200
                  border-neutral-300 dark:border-neutral-600
                  focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20
                  disabled:bg-neutral-100 dark:disabled:bg-neutral-800
                  disabled:cursor-not-allowed
                "
                disabled={isSaving}
              >
                <option value="manual">Manual</option>
                <option value="cron">Cron</option>
                <option value="rate">Rate</option>
              </select>
            </div>

            {(formData.schedule.type === 'cron' || formData.schedule.type === 'rate') && (
              <>
                <div className="mb-4">
                  <label htmlFor="scheduleExpression" className="
                    block mb-2 text-sm font-medium text-neutral-700 dark:text-neutral-300
                  ">
                    {formData.schedule.type === 'cron' ? 'Cron Expression' : 'Rate Expression'} *
                  </label>
                  <input
                    id="scheduleExpression"
                    type="text"
                    value={formData.schedule.expression || ''}
                    onChange={(e) => handleScheduleChange('expression', e.target.value)}
                    className="
                      w-full px-3 py-2.5 border rounded-md text-sm
                      transition-all duration-200
                      bg-white dark:bg-neutral-900
                      text-neutral-800 dark:text-neutral-200
                      border-neutral-300 dark:border-neutral-600
                      focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20
                      disabled:bg-neutral-100 dark:disabled:bg-neutral-800
                      disabled:cursor-not-allowed
                    "
                    disabled={isSaving}
                    placeholder={formData.schedule.type === 'cron' ? '0 0 * * *' : 'rate(1 hour)'}
                    required={formData.schedule.type === 'cron' || formData.schedule.type === 'rate'}
                  />
                </div>

                <div className="mb-4">
                  <label htmlFor="timezone" className="
                    block mb-2 text-sm font-medium text-neutral-700 dark:text-neutral-300
                  ">
                    Timezone
                  </label>
                  <select
                    id="timezone"
                    value={formData.schedule.timezone}
                    onChange={(e) => handleScheduleChange('timezone', e.target.value)}
                    className="
                      w-full px-3 py-2.5 border rounded-md text-sm
                      transition-all duration-200
                      bg-white dark:bg-neutral-900
                      text-neutral-800 dark:text-neutral-200
                      border-neutral-300 dark:border-neutral-600
                      focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20
                      disabled:bg-neutral-100 dark:disabled:bg-neutral-800
                      disabled:cursor-not-allowed
                    "
                    disabled={isSaving}
                  >
                    {/* THUB-148: keep this list in sync with TaskScheduler.tsx.
                        A shorter list here silently dropped the selected value
                        when editing a task whose timezone was not listed. */}
                    <option value="UTC">UTC</option>
                    <option value="America/New_York">America/New_York</option>
                    <option value="America/Chicago">America/Chicago</option>
                    <option value="America/Denver">America/Denver</option>
                    <option value="America/Los_Angeles">America/Los_Angeles</option>
                    <option value="Europe/London">Europe/London</option>
                    <option value="Europe/Paris">Europe/Paris</option>
                    <option value="Europe/Berlin">Europe/Berlin</option>
                    <option value="Asia/Tokyo">Asia/Tokyo</option>
                    <option value="Asia/Shanghai">Asia/Shanghai</option>
                    <option value="Asia/Kolkata">Asia/Kolkata</option>
                    <option value="Australia/Sydney">Australia/Sydney</option>
                  </select>
                </div>

                <div className="mb-4">
                  <label className="
                    flex items-center gap-2 cursor-pointer
                    text-sm font-medium text-neutral-700 dark:text-neutral-300
                  ">
                    <input
                      type="checkbox"
                      checked={formData.schedule.enabled}
                      onChange={(e) => handleScheduleChange('enabled', e.target.checked)}
                      disabled={isSaving}
                      className="w-4 h-4 accent-primary-500"
                    />
                    Enable Schedule
                  </label>
                </div>
              </>
            )}
          </div>
        )}

        <div className="mb-8 last:mb-0">
          <h3 className="
            m-0 mb-4 text-lg font-semibold text-neutral-700 dark:text-neutral-300
            border-b border-neutral-200 dark:border-neutral-700 pb-2
          ">
            Transfer Configuration
          </h3>

          {/* Remote path - only for remote connections. Wording is transport-aware
              (SFTP directory / Azure blob prefix / HTTP resource path) -- THUB-220. */}
          {selectedHostingType === 'remote' && (() => {
            const pathWording = remotePathWording(selectedConnection?.transferMethod);
            return (
            <div className="mb-4">
              {formData.transferDirection === 'inbound' ? (
                <>
                  <label htmlFor="sourceDirectory" className="
                    block mb-2 text-sm font-medium text-neutral-700 dark:text-neutral-300
                  ">
                    {pathWording.sourceLabel}
                  </label>
                  <input
                    id="sourceDirectory"
                    type="text"
                    value={formData.configuration.sourceDirectory || ''}
                    onChange={(e) => handleConfigurationChange('sourceDirectory', e.target.value)}
                    className="
                      w-full px-3 py-2.5 border rounded-md text-sm
                      transition-all duration-200
                      bg-white dark:bg-neutral-900
                      text-neutral-800 dark:text-neutral-200
                      border-neutral-300 dark:border-neutral-600
                      focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20
                      disabled:bg-neutral-100 dark:disabled:bg-neutral-800
                      disabled:cursor-not-allowed
                    "
                    disabled={isSaving}
                    placeholder="/outbound or /data/exports"
                  />
                  <small className="block mt-1.5 text-neutral-500 dark:text-neutral-400 text-xs">
                    {pathWording.sourceHelp}
                  </small>
                </>
              ) : (
                <>
                  <label htmlFor="destinationDirectory" className="
                    block mb-2 text-sm font-medium text-neutral-700 dark:text-neutral-300
                  ">
                    {pathWording.destinationLabel}
                  </label>
                  <input
                    id="destinationDirectory"
                    type="text"
                    value={formData.configuration.destinationDirectory || ''}
                    onChange={(e) => handleConfigurationChange('destinationDirectory', e.target.value)}
                    className="
                      w-full px-3 py-2.5 border rounded-md text-sm
                      transition-all duration-200
                      bg-white dark:bg-neutral-900
                      text-neutral-800 dark:text-neutral-200
                      border-neutral-300 dark:border-neutral-600
                      focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20
                      disabled:bg-neutral-100 dark:disabled:bg-neutral-800
                      disabled:cursor-not-allowed
                    "
                    disabled={isSaving}
                    placeholder="/inbound or /data/imports"
                  />
                  <small className="block mt-1.5 text-neutral-500 dark:text-neutral-400 text-xs">
                    {pathWording.destinationHelp}
                  </small>
                </>
              )}
            </div>
            );
          })()}

          {/* Hosted path info */}
          {selectedHostingType === 'hosted' && (
            <div className="mb-4 p-3 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-md">
              <p className="m-0 text-sm text-neutral-600 dark:text-neutral-400">
                File paths are automatically managed based on the task prefix.
                {formData.transferDirection === 'inbound'
                  ? ' Partners upload files to the inbound directory on our SFTP server.'
                  : ' Value stream teams drop files to the staging directory, and partners download from the outbound directory.'}
              </p>
            </div>
          )}

          <div className="mb-4">
            <label htmlFor="filePattern" className="
              block mb-2 text-sm font-medium text-neutral-700 dark:text-neutral-300
            ">
              File Pattern
            </label>
            <input
              id="filePattern"
              type="text"
              value={formData.configuration.filePattern || ''}
              onChange={(e) => handleConfigurationChange('filePattern', e.target.value)}
              className="
                w-full px-3 py-2.5 border rounded-md text-sm
                transition-all duration-200
                bg-white dark:bg-neutral-900
                text-neutral-800 dark:text-neutral-200
                border-neutral-300 dark:border-neutral-600
                focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20
                disabled:bg-neutral-100 dark:disabled:bg-neutral-800
                disabled:cursor-not-allowed
              "
              disabled={isSaving}
              placeholder="*.txt, *.csv, etc."
            />
          </div>

          <div className="mb-4">
            <label className="
              flex items-center gap-2 cursor-pointer
              text-sm font-medium text-neutral-700 dark:text-neutral-300
            ">
              <input
                type="checkbox"
                checked={formData.configuration.notifyOnCompletion || false}
                onChange={(e) => handleConfigurationChange('notifyOnCompletion', e.target.checked)}
                disabled={isSaving}
                className="w-4 h-4 accent-primary-500"
              />
              Notify on completion
            </label>
          </div>

          <div className="mb-4">
            <label className="
              flex items-center gap-2 cursor-pointer
              text-sm font-medium text-neutral-700 dark:text-neutral-300
            ">
              <input
                type="checkbox"
                checked={formData.configuration.notifyOnError || false}
                onChange={(e) => handleConfigurationChange('notifyOnError', e.target.checked)}
                disabled={isSaving}
                className="w-4 h-4 accent-primary-500"
              />
              Notify on error
            </label>
          </div>

          {/* THUB-203: only render when the connection has the matching PGP key
              (or the task already has PGP on, so it can be turned off). */}
          {showPgpToggle && (
            <div className="mb-4">
              <label className="
                flex items-center gap-2 cursor-pointer
                text-sm font-medium text-neutral-700 dark:text-neutral-300
              ">
                <input
                  type="checkbox"
                  checked={pgpEnabled}
                  onChange={(e) => setPgpEnabled(e.target.checked)}
                  disabled={isSaving}
                  className="w-4 h-4 accent-primary-500"
                />
                Enable PGP encryption
              </label>
              <small className="block mt-1.5 text-neutral-500 dark:text-neutral-400 text-xs">
                {formData.transferDirection === 'outbound'
                  ? 'Files will be PGP-encrypted before delivery to partner'
                  : 'Incoming files will be PGP-decrypted after receipt'}
              </small>
            </div>
          )}

          {onValidateConfiguration && (
            <div className="mb-4">
              <button
                type="button"
                onClick={handleValidateConfiguration}
                className="
                  px-4 py-2
                  bg-neutral-100 dark:bg-slate-700
                  border border-neutral-300 dark:border-slate-600
                  text-neutral-700 dark:text-neutral-200
                  rounded-md text-sm font-medium cursor-pointer
                  transition-colors duration-200
                  hover:bg-neutral-200 dark:hover:bg-slate-600
                  disabled:opacity-60 disabled:cursor-not-allowed
                "
                disabled={isSaving || isValidating}
              >
                {isValidating ? 'Validating...' : 'Validate Configuration'}
              </button>
            </div>
          )}
        </div>

        {/* T090: Post-Pickup Action Section - Only show for Remote Inbound tasks */}
        {isRemoteInboundTask() && (
          <div className="mb-8 last:mb-0 border-l-[3px] border-primary-500 pl-4">
            <h3 className="
              m-0 mb-4 text-lg font-semibold text-neutral-700 dark:text-neutral-300
              border-b border-neutral-200 dark:border-neutral-700 pb-2
            ">
              Post-Pickup Action
            </h3>
            <p className="m-0 mb-4 text-neutral-500 dark:text-neutral-400 text-sm leading-relaxed">
              Choose what happens to files on the partner server after they are successfully picked up.
            </p>
            <div className="mb-4">
              <label htmlFor="postPickupAction" className="
                block mb-2 text-sm font-medium text-neutral-700 dark:text-neutral-300
              ">
                Action After Pickup
              </label>
              <select
                id="postPickupAction"
                value={postPickupAction}
                onChange={(e) => setPostPickupAction(e.target.value as PostPickupAction)}
                className="
                  w-full px-3 py-2.5 border rounded-md text-sm
                  transition-all duration-200
                  bg-white dark:bg-neutral-900
                  text-neutral-800 dark:text-neutral-200
                  border-neutral-300 dark:border-neutral-600
                  focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20
                  disabled:bg-neutral-100 dark:disabled:bg-neutral-800
                  disabled:cursor-not-allowed
                "
                disabled={isSaving}
              >
                <option value="none">None - Leave file on partner server</option>
                <option value="delete">Delete - Remove file after pickup</option>
                <option value="move">Move - Move file to archive folder</option>
              </select>
            </div>

            {postPickupAction === 'move' && (
              <div className="mb-4">
                <label htmlFor="postPickupTargetPath" className="
                  block mb-2 text-sm font-medium text-neutral-700 dark:text-neutral-300
                ">
                  Target Folder Path
                </label>
                <input
                  id="postPickupTargetPath"
                  type="text"
                  value={postPickupTargetPath}
                  onChange={(e) => setPostPickupTargetPath(e.target.value)}
                  placeholder="/archive"
                  className="
                    w-full px-3 py-2.5 border rounded-md text-sm
                    transition-all duration-200
                    bg-white dark:bg-neutral-900
                    text-neutral-800 dark:text-neutral-200
                    border-neutral-300 dark:border-neutral-600
                    focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20
                    disabled:bg-neutral-100 dark:disabled:bg-neutral-800
                    disabled:cursor-not-allowed
                  "
                  disabled={isSaving}
                />
                <small className="block mt-1.5 text-neutral-500 dark:text-neutral-400 text-xs leading-relaxed">
                  The folder on the partner server where files will be moved after pickup.
                </small>
              </div>
            )}
          </div>
        )}

        {/* FR-042/043: Delivery Mode Section - Only show for Remote Outbound tasks */}
        {isRemoteOutboundTask() && (
          <div className="mb-8 last:mb-0 border-l-[3px] border-primary-500 pl-4">
            <h3 className="
              m-0 mb-4 text-lg font-semibold text-neutral-700 dark:text-neutral-300
              border-b border-neutral-200 dark:border-neutral-700 pb-2
            ">
              Delivery Mode
            </h3>
            <p className="m-0 mb-4 text-neutral-500 dark:text-neutral-400 text-sm leading-relaxed">
              Choose when files should be delivered to the partner server.
            </p>
            <div className="flex flex-col gap-3">
              {deliveryModeOptions.map((type) => (
                <label
                  key={type}
                  className={`
                    flex items-start gap-3 p-3 cursor-pointer
                    bg-white dark:bg-neutral-900
                    border rounded-md
                    transition-all duration-200
                    ${deliveryMode === type
                      ? 'border-primary-500 bg-primary-50/50 dark:bg-primary-900/10'
                      : 'border-neutral-300 dark:border-neutral-600 hover:border-neutral-400 dark:hover:border-neutral-500 hover:bg-neutral-50 dark:hover:bg-neutral-800'
                    }
                  `}
                >
                  <input
                    type="radio"
                    name="deliveryMode"
                    value={type}
                    checked={deliveryMode === type}
                    onChange={(e) => setDeliveryMode(e.target.value as DeliveryMode)}
                    disabled={isSaving}
                    className="mt-1 w-4 h-4 accent-primary-500"
                  />
                  <span className="flex flex-col gap-1">
                    <strong className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </strong>
                    <small className="text-neutral-500 dark:text-neutral-400 text-[13px] leading-relaxed">
                      {type === 'immediate' && 'Deliver as soon as file is ready in the internal bucket'}
                      {type === 'scheduled' && 'Batch and deliver on a schedule (EventBridge cron/rate)'}
                    </small>
                  </span>
                </label>
              ))}
            </div>

            {/* Delivery schedule input when scheduled */}
            {deliveryMode === 'scheduled' && (
              <div className="mt-4">
                <label htmlFor="deliverySchedule" className="
                  block mb-2 text-sm font-medium text-neutral-700 dark:text-neutral-300
                ">
                  Delivery Schedule Expression
                </label>
                <input
                  id="deliverySchedule"
                  type="text"
                  value={deliverySchedule}
                  onChange={(e) => setDeliverySchedule(e.target.value)}
                  placeholder="rate(1 hour) or cron(0 8 * * ? *)"
                  className="
                    w-full px-3 py-2.5 border rounded-md text-sm font-mono
                    transition-all duration-200
                    bg-white dark:bg-neutral-900
                    text-neutral-800 dark:text-neutral-200
                    border-neutral-300 dark:border-neutral-600
                    focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20
                    disabled:bg-neutral-100 dark:disabled:bg-neutral-800
                    disabled:cursor-not-allowed
                  "
                  disabled={isSaving}
                />
                <small className="block mt-1.5 text-neutral-500 dark:text-neutral-400 text-xs">
                  EventBridge cron or rate expression. Example: <code>rate(1 hour)</code>, <code>cron(0 8 * * ? *)</code>
                </small>
              </div>
            )}
          </div>
        )}

        <div className="
          flex gap-4 justify-end
          pt-6 mt-8 border-t border-neutral-200 dark:border-slate-700
        ">
          <button
            type="button"
            onClick={onCancel}
            className="
              px-6 py-2.5
              bg-neutral-100 dark:bg-slate-700
              border border-neutral-300 dark:border-slate-600
              text-neutral-700 dark:text-neutral-200
              rounded-md text-sm font-medium cursor-pointer
              transition-colors duration-200
              hover:bg-neutral-200 dark:hover:bg-slate-600
              disabled:opacity-60 disabled:cursor-not-allowed
            "
            disabled={isSaving}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="
              px-6 py-2.5
              bg-primary-500 hover:bg-primary-600
              border-none text-white
              rounded-md text-sm font-medium cursor-pointer
              transition-colors duration-200
              disabled:opacity-60 disabled:cursor-not-allowed
            "
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
}